<template>
  <b-modal
    id="modal-separate-row-preview"
    ref="modalRef"
    :title="$t('e_invoice_detail')"
    size="xl"
    centered
    no-close-on-backdrop
    hide-header-close
    @hide="handleHide"
  >
    <div class="invoice-preview-container">
      <b-card class="mb-0" no-body>
        <div class="invoice-header d-flex justify-content-between flex-md-row flex-column p-2">
          <div class="invoice-title">
            <h4>{{ $t('invoice_preview') }}</h4>
            <p class="text-muted mb-0">{{ $t('invoice_with_separate_charge_row') }}</p>
          </div>
        </div>

        <hr class="invoice-spacing" />

        <!-- Invoice Items Details -->
        <b-table
          :items="previewItems"
          :fields="tableFields"
          class="invoice-detail-items"
          responsive
          borderless
          show-empty
        >
          <template #cell(itemName)="data">
            <div class="d-flex align-items-center">
              <span>{{ data.item.ItemName }}</span>
              <small v-if="data.item.IsChargeRow" class="badge badge-info ml-1">
                {{ $t('fee_row') }}
              </small>
            </div>
          </template>
          
          <template #cell(unitName)="data">
            {{ data.item.UnitName }}
          </template>
          
          <template #cell(quantity)="data">
            <div class="text-right">{{ data.item.Quantity > 0 ? formatNumber(data.item.Quantity) : '' }}</div>
          </template>
          
          <template #cell(price)="data">
            <div class="text-right">{{ formatMoney(data.item.Price) }}</div>
          </template>
          
          <template #cell(discountAmount)="data" v-if="showDiscountColumn">
            <div class="text-right">{{ formatMoney(data.item.DiscountAmount) }}</div>
          </template>
          
          <template #cell(chargePercent)="data" v-if="showChargePercentColumn">
            <div class="text-right">{{ data.item.Charges > 0 ? formatNumber(data.item.Charges) + '%' : '' }}</div>
          </template>
          
          <template #cell(chargeAmount)="data" v-if="showChargeColumn">
            <div class="text-right">{{ formatMoney(data.item.ChargesAmount) }}</div>
          </template>
          
          <template #cell(taxPercent)="data" v-if="showTaxPercentColumn">
            <div class="text-right">{{ data.item.Tax > 0 ? formatNumber(data.item.Tax) + '%' : '' }}</div>
          </template>
          
          <template #cell(taxAmount)="data" v-if="showTaxColumn">
            <div class="text-right">{{ formatMoney(data.item.TaxAmount) }}</div>
          </template>
          
          <template #cell(totalAmount)="data">
            <div class="text-right">{{ formatMoney(data.item.TotalAmount) }}</div>
          </template>

          <template #custom-foot>
            <b-tr>
              <b-td :colspan="getColspan()" class="text-right font-weight-bold">
                {{ $t('common_total') }}:
              </b-td>
              <b-td class="text-right font-weight-bold">
                {{ formatMoney(totalAmount) }}
              </b-td>
            </b-tr>
          </template>
        </b-table>
      </b-card>
    </div>

    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="$bvModal.hide('modal-separate-row-preview')"
      >
        {{ $t('common_cancel') }}
      </b-button>
      <b-button
        variant="primary"
        @click="handleConfirm"
      >
        {{ $t('common_confirm') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    ToastificationContent
  },
  props: {
    invoiceData: {
      type: Object,
      required: false,
      default: () => ({})
    },
    invoiceItems: {
      type: Array,
      required: false,
      default: () => []
    },
    actionType: {
      type: String,
      required: false,
      default: 'ADD_INVOICE'
    }
  },
  data() {
    return {
      isOpen: false,
      previewItems: []
    }
  },
  computed: {
    ...mapState({
      $ls_EInvoiceConfig: (state) => state.eInvoiceManagement.eInvoiceConfig
    }),
    
    showDiscountColumn() {
      return this.$ls_EInvoiceConfig.DiscountOption === 'SEPARATE_COLUMN';
    },
    
    showChargeColumn() {
      return this.$ls_EInvoiceConfig.ChargeOption === 'SEPARATE_COLUMN' || 
             this.$ls_EInvoiceConfig.ChargeOption === 'DISPLAY_IN_TOTAL' ||
             this.$ls_EInvoiceConfig.ChargeOption === 'SEPARATE_ROW';
    },
    
    showChargePercentColumn() {
      return this.$ls_EInvoiceConfig.ShowChargePercent === true;
    },
    
    showTaxColumn() {
      return this.$ls_EInvoiceConfig.TaxOption === 'SEPARATE_COLUMN' && 
             this.$ls_EInvoiceConfig.InvoiceType !== 'SALES_INVOICE';
    },
    
    showTaxPercentColumn() {
      return this.$ls_EInvoiceConfig.ShowTaxPercent === true && 
             this.$ls_EInvoiceConfig.InvoiceType !== 'SALES_INVOICE';
    },
    
    tableFields() {
      const fields = [
        { key: 'itemName', label: this.$t('product_service_name') },
        { key: 'unitName', label: this.$t('common_unit') },
        { key: 'quantity', label: this.$t('common_quantity_abbreviation') },
        { key: 'price', label: this.$t('common_unit_price') }
      ];
      
      if (this.showDiscountColumn) {
        fields.push({ key: 'discountAmount', label: this.$t('discount_amount') });
      }
      
      if (this.showChargePercentColumn) {
        fields.push({ key: 'chargePercent', label: this.$t('common_charge') });
      }
      
      if (this.showChargeColumn) {
        fields.push({ key: 'chargeAmount', label: this.$t('common_charge_amount') });
      }
      
      if (this.showTaxPercentColumn) {
        fields.push({ key: 'taxPercent', label: this.$t('common_tax') });
      }
      
      if (this.showTaxColumn) {
        fields.push({ key: 'taxAmount', label: this.$t('common_tax_amount') });
      }
      
      fields.push({ key: 'totalAmount', label: this.$t('common_amount') });
      
      return fields;
    },
    
    totalAmount() {
      return this.previewItems.reduce((sum, item) => sum + (+item.TotalAmount || 0), 0);
    }
  },
  methods: {
    getColspan() {
      return this.tableFields.length - 1;
    },
    
    formatNumber(value, decimals = 0) {
      if (!value && value !== 0) return '';
      
      return new Intl.NumberFormat('vi-VN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    },
    
    formatMoney(value) {
      if (!value && value !== 0) return '';
      
      const decimals = this.$ls_EInvoiceConfig?.MinorUnit ?? 0;
      return new Intl.NumberFormat('vi-VN', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    },
    
    showModal(invoiceData, invoiceItems) {
      // Lưu dữ liệu để hiển thị
      this.previewItems = [...invoiceItems];
      
      // Mở modal
      this.isOpen = true;
      this.$nextTick(() => {
        if (this.$refs.modalRef) {
          this.$refs.modalRef.show();
        } else {
          this.$bvModal.show('modal-separate-row-preview');
        }
      });
    },
    
    hideModal() {
      this.isOpen = false;
      if (this.$refs.modalRef) {
        this.$refs.modalRef.hide();
      } else {
        this.$bvModal.hide('modal-separate-row-preview');
      }
    },
    
    handleHide() {
      this.$emit('cancel');
    },
    
    handleConfirm() {
      this.$emit('confirm');
      this.hideModal();
    }
  }
}
</script>

<style scoped>
.invoice-preview-container {
  max-height: 60vh;
  overflow-y: auto;
}

.invoice-spacing {
  margin: 1.5rem 0;
}

.invoice-detail-items {
  min-width: 600px;
}

/* Styling for charge rows */
:deep(.table tr.charge-row) {
  background-color: #f8f9fa;
}

:deep(.table tr.charge-row td) {
  font-style: italic;
}

.modal-body {
  padding: 0;
}
</style>
